import { useEffect, useRef, useCallback } from "react";
import { useTheme } from "@mui/material/styles";

export default function UploadWidget(props) {
  const { folder, max, onComplete, children } = props;
  const theme = useTheme();

  const cloudinaryRef = useRef();
  const widgetRef = useRef();
  const onCompleteRef = useRef(onComplete);
  const triggerRef = useRef(null);

  const MAX_FILES = 5;

  // Keep onComplete ref up to date without causing re-renders
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Lazy initialization - only create widget when needed
  const getOrCreateWidget = useCallback(() => {
    if (!widgetRef.current) {
      cloudinaryRef.current = window.cloudinary;
      widgetRef.current = cloudinaryRef.current.createUploadWidget(
        {
          cloudName: "draqf7k7f",
          uploadPreset: "saas-upload",
          folder: folder,
          sources: ["local", "camera"],
          max_files: max || MAX_FILES,
          clientAllowedFormats: ["image"],
          maxFileSize: 10000000, // 10MB
          showPoweredBy: false,
          //autoMinimize: true,
          singleUploadAutoClose: true,
          showUploadMoreButton: false,
          language: "en",
          text: {
            en: {
              local: {
                dd_title_multi: "Drag & Drop Photos",
                //browse: "SELECT PHOTOS"
              },
              notifications: {
                limit_reached: `Can't upload more than ${max || MAX_FILES} photos`,
              },
              menu: {
                files: "Device Files",
              },
            },
          },
          styles: {
            palette: {
              window: "#ffffff",
              sourceBg: "#f4f4f5",
              windowBorder: "#90a0b3",
              tabIcon: "#000000",
              inactiveTabIcon: "#555a5f",
              menuIcons: "#555a5f",
              link: theme.palette.primary.main,
              action: theme.palette.primary.main,
              inProgress: "#0433ff",
              complete: theme.palette.primary.main,
              error: "#F44235",
              textDark: "#000000",
              textLight: "#fcfffd",
            },
            fonts: {
              default: null,
              "sans-serif": {
                url: null,
                active: true,
              },
            },
          },
        },
        function (error, result) {
          console.log(result, error);
          // Restore focus when widget closes
          if (result.event === "close") {
            triggerRef.current?.focus();
          }
          // Handle the result or error here
          if (result.event === "queues-end") {
            onCompleteRef.current(result.info.files?.map((o) => o.uploadInfo.secure_url));
          }
        }
      );
    }
    return widgetRef.current;
  }, [folder, max, theme.palette.primary.main]);

  const openWidget = () => {
    getOrCreateWidget().open();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openWidget();
    }
  };

  return (
    <span
      ref={triggerRef}
      onClick={openWidget}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      {children}
    </span>
  );
}
